import React, { SyntheticEvent, useEffect, useMemo, useState } from "react";
import { Autocomplete, AutocompletePropsSizeOverrides, Box, TextField, createFilterOptions } from "@mui/material";
import { OverridableStringUnion } from "@mui/types";
import ct from "countries-and-timezones";

// https://betterprogramming.pub/how-to-detect-emojis-with-javascript-1a0342be9af7
function getFlagEmoji(countryCode: string) {
  const codePoints = countryCode
    .toUpperCase()
    .split("")
    .map((char) => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
}

function getOptionLabel(option: CountryTimeZoneOption): string {
  if (option.labelOverride) return option.labelOverride;

  const parts: Array<string> = [];
  if (option.countryFlag) parts.push(option.countryFlag);
  if (option.timeZone) parts.push(option.timeZone);
  if (option.timeZoneOffset) parts.push(`(${option.timeZoneOffset})`);
  return parts.join(" ");
}

interface CountryTimeZoneOption {
  timeZone: string;
  countryCode?: string;
  countryFlag?: string;
  countryName?: string;
  group?: string;
  labelOverride?: string | undefined;
  timeZoneOffset?: string;
}

interface TimezoneAutocompleteProps {
  /**
   * The currently selected locale (e.g. "en-US").  This property must be externally managed.
   * @default navigator.language
   */
  locale?: string;
  /**
   * The currently selected time zone (e.g. "America/Chicago").  This property must be externally managed.
   */
  timeZone?: string;
  /**
   * Callback fired when the time zone is changed.
   */
  onChange: (timeZone: string) => void;
  /**
   * Callback fired when the default time zone is set.
   */
  onDefault?(timeZone: string): void;
  /**
   * The size of the component.
   * @default 'medium'
   */
  size?: OverridableStringUnion<'small' | 'medium', AutocompletePropsSizeOverrides>;

}

export default function TimezoneAutocomplete(props: TimezoneAutocompleteProps): React.JSX.Element {
  const [timeZone, setTimezone] = useState(props.timeZone);
  const timeZones = useMemo(buildTimeZones, [props.locale, navigator.language]);
  const selectedTimeZone = useMemo(getSelectedTimeZone, [timeZones, timeZone]);

  function buildTimeZones(): Array<CountryTimeZoneOption> {
    const locale = props.locale || navigator.language;
    const countryTimezones = Object.values(ct.getAllCountries());
    const timeZones = countryTimezones.reduce((memo: Array<CountryTimeZoneOption>, current: ct.Country) => {
      for (const timeZone of current.timezones) {
        const countryFlag = getFlagEmoji(current.id);
        const countryName = current.name;
        const timeZoneParts = Intl.DateTimeFormat(locale, {timeZoneName: "shortOffset", timeZone: timeZone}).formatToParts();
        const timeZoneOffset = timeZoneParts.find((value) => value.type === "timeZoneName")?.value;
        const entry: CountryTimeZoneOption = {
          countryCode: current.id,
          countryFlag,
          countryName,
          group: `${countryFlag} ${countryName}`,
          timeZone: timeZone,
          timeZoneOffset
        };
        memo.push(entry);
      }
      return memo;
    }, []);
    const defaultTimezone: CountryTimeZoneOption = {
      labelOverride: "Browser Default",
      timeZone: ""
    };
    timeZones.unshift(defaultTimezone);
    return timeZones;
  }

  function getSelectedTimeZone(): CountryTimeZoneOption {
    const found = timeZones.find((option) => option.timeZone === timeZone);
    return found || timeZones[0];
  }

  function handleTimeZoneChange(event: SyntheticEvent, countryTimeZoneOption: CountryTimeZoneOption): void {
    setTimezone(countryTimeZoneOption.timeZone);
    props.onChange(countryTimeZoneOption.timeZone);
  }

  // Setup the fields we want to be able to filter on
  const filterOptions = createFilterOptions({
    stringify: (option: CountryTimeZoneOption) => [
      option.countryCode,
      option.countryFlag,
      option.countryName,
      option.timeZone,
      option.timeZoneOffset
    ].join()
  });

  useEffect(() => {
    props.onDefault && props.onDefault(selectedTimeZone.timeZone);
  }, []);

  return (
    <Autocomplete
      fullWidth={true}
      disableClearable
      filterOptions={filterOptions}
      getOptionLabel={getOptionLabel}
      groupBy={(option) => option.group || ""}
      onChange={handleTimeZoneChange}
      options={timeZones}
      size={props.size}
      isOptionEqualToValue={(option: CountryTimeZoneOption, value: CountryTimeZoneOption) => option.timeZone === value.timeZone}
      value={selectedTimeZone}
      renderInput={(params) => <TextField autoComplete="off" {...params} label="" />}
      renderOption={(props, option) => {
        return (
          <Box component="li" {...props}>
            {option.labelOverride || (`${option.timeZone} (${option.timeZoneOffset})`)}
          </Box>
        )
      }} />
  )
}
